"use client";

import { useEffect, useState, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AnimatedBackground } from "@/components/shared/animated-background";
import { apiGet, apiPost, setAuthToken, clearAuthToken } from "@/lib/api";
import { useAppStore } from "@/store/app";
import {
  Sparkles, Globe2, Map, Bot, GitBranch, BrainCircuit, Swords, Trophy,
  ArrowRight, Loader2, Rocket, Target, Wand2, ChevronRight,
} from "lucide-react";

const DREAM_EXAMPLES = [
  "I want to build an AI company.",
  "I want to become a game developer.",
  "I want to learn Python.",
  "I want to create a YouTube channel.",
  "I want to build my own game.",
  "I want to become an entrepreneur.",
];

const FEATURES = [
  { icon: Sparkles, title: "AI that actually builds", desc: "Not a chatbot beside your app — the intelligence inside it. It creates goals, roadmaps and projects with real tool calls.", accent: "from-violet-500/20 to-violet-500/5" },
  { icon: Globe2, title: "Evolving Future World", desc: "Your dream becomes a living city. Buildings rise as you ship projects; skill towers grow as you learn; districts unlock at milestones.", accent: "from-cyan-500/20 to-cyan-500/5" },
  { icon: Map, title: "Roadmaps & milestones", desc: "Vague ambitions become sequenced plans: milestones, projects, weekly tasks and daily missions that compound.", accent: "from-emerald-500/20 to-emerald-500/5" },
  { icon: Bot, title: "Six AI mentors", desc: "Coding, game-dev, startup, design, learning and strategy mentors — each with a distinct mind, all one intelligence.", accent: "from-amber-500/20 to-amber-500/5" },
  { icon: GitBranch, title: "Simulation engine", desc: "Ask 'what if I change my plan?' and compare scenario A/B/C: work, risks, skills, complexity and bottlenecks.", accent: "from-rose-500/20 to-rose-500/5" },
  { icon: BrainCircuit, title: "Persistent memory", desc: "The AI remembers your goals, preferences, decisions and progress between sessions — and forgets nothing you teach it.", accent: "from-violet-500/20 to-cyan-500/5" },
  { icon: Swords, title: "Challenges & missions", desc: "Weekly challenges and daily missions keep momentum alive. XP, levels and streaks make progress feel like a game.", accent: "from-amber-500/20 to-rose-500/5" },
  { icon: Trophy, title: "Achievements that mean it", desc: "Every trophy maps to real shipped work — first steps, streaks, skill mastery, dreams realized.", accent: "from-emerald-500/20 to-violet-500/5" },
];

function LoginDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { setSession } = useAppStore();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await apiPost<{ user: { id: string; name: string; email: string; level: number; xp: number; streak: number }; hasGoal: boolean; token?: string }>("/api/auth/login", { name, email });
      if (data.token) setAuthToken(data.token);
      setSession(data.user);
      const next = searchParams.get("next");
      router.push(next && next.startsWith("/") ? next : data.hasGoal ? "/dashboard" : "/dream/new");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not enter. Try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong max-w-md rounded-3xl border-primary/20">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-400 text-2xl font-black text-black shadow-[0_0_30px_rgba(139,92,246,0.5)]">✦</div>
          <DialogTitle className="font-display text-xl font-bold">Enter Dream Builder AI</DialogTitle>
          <DialogDescription>
            One identity. Your dreams, worlds and progress — saved forever.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">What should the AI call you?</Label>
            <Input id="name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} className="h-11 rounded-xl bg-white/5" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 rounded-xl bg-white/5" />
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
          <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-[0_0_24px_rgba(139,92,246,0.4)] hover:from-violet-400 hover:to-violet-500">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-2 h-4 w-4" />Enter the Future</>}
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">No password needed — this is your personal workspace.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TypeCycler() {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = DREAM_EXAMPLES[index % DREAM_EXAMPLES.length];
    const speed = deleting ? 22 : 42;
    let timer: ReturnType<typeof setTimeout>;
    if (!deleting && text === current) {
      timer = setTimeout(() => setDeleting(true), 2200);
    } else if (deleting && text === "") {
      timer = setTimeout(() => {
        setDeleting(false);
        setIndex((i) => (i + 1) % DREAM_EXAMPLES.length);
      }, 300);
    } else {
      timer = setTimeout(
        () => setText(deleting ? current.slice(0, text.length - 1) : current.slice(0, text.length + 1)),
        speed
      );
    }
    return () => clearTimeout(timer);
  }, [text, deleting, index]);

  return (
    <span className="text-gradient font-semibold">
      {text}
      <span className="animate-blink text-primary">▊</span>
    </span>
  );
}

function LandingPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loginOpen, setLoginOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const { setSession } = useAppStore();

  const check = useCallback(async () => {
    try {
      const data = await apiGet<{ user: { id: string; name: string; email: string; level: number; xp: number; streak: number } | null; ai?: { configured: boolean } }>("/api/auth/me");
      if (data.user) {
        setSession(data.user, data.ai);
        setLoggedIn(true);
      } else if (searchParams.get("login")) {
        setLoginOpen(true);
      }
    } catch {
      /* stay on landing */
    }
    setChecking(false);
  }, [searchParams, setSession]);

  useEffect(() => {
    const timer = setTimeout(check, 0);
    return () => clearTimeout(timer);
  }, [check]);

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <AnimatedBackground variant="landing" />

      {/* Nav */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 font-black text-black shadow-[0_0_20px_rgba(139,92,246,0.5)]">✦</div>
          <div className="leading-tight">
            <div className="font-display text-base font-bold">Dream Builder AI</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Future Operating System</div>
          </div>
        </div>
        <Button
          onClick={() => (loggedIn ? router.push("/dashboard") : setLoginOpen(true))}
          className="rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:from-violet-400"
        >
          {loggedIn ? "Enter your world" : "Enter"}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-5xl px-6 pt-14 text-center sm:pt-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-violet-200">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            One AI. Three minds. Infinite futures.
          </div>
          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            Don&apos;t just dream.
            <br />
            <span className="text-gradient">Build the future.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Describe what you want to become. Dream Builder AI transforms it into an interactive world —
            with a roadmap, projects, skills, mentors, challenges and simulations that evolve as you progress.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              onClick={() => (loggedIn ? router.push("/dream/new") : setLoginOpen(true))}
              className="h-13 rounded-2xl bg-gradient-to-r from-violet-500 to-cyan-500 px-8 text-base font-semibold text-black shadow-[0_0_36px_rgba(139,92,246,0.45)] hover:from-violet-400 hover:to-cyan-400"
            >
              <Rocket className="mr-2 h-5 w-5" />
              {loggedIn ? "Build a new dream" : "Start building — free"}
            </Button>
            <Button size="lg" variant="outline" onClick={() => setLoginOpen(true)} className="h-13 rounded-2xl border-primary/30 bg-white/5 px-8 text-base backdrop-blur hover:bg-white/10">
              I already have a world
            </Button>
          </div>
          <div className="glass mx-auto mt-10 max-w-xl rounded-2xl px-6 py-4 text-left text-sm text-muted-foreground sm:text-base">
            <span className="mr-2 inline-flex items-center gap-1 text-primary"><Target className="h-4 w-4" /></span>
            <TypeCycler />
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="relative z-10 mx-auto mt-24 max-w-6xl px-6">
        <div className="mb-10 text-center">
          <h2 className="font-display text-2xl font-bold sm:text-4xl">From sentence to civilization</h2>
          <p className="mt-3 text-muted-foreground">Three steps. The AI does the architecture; you do the becoming.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { step: "01", title: "Speak your dream", desc: "One sentence is enough. 'I want to build an AI company.' The AI asks only what it truly needs.", icon: Wand2 },
            { step: "02", title: "Watch it build", desc: "The orchestrator plans, a reviewer validates, and your world materializes — milestones, projects, skills, challenges, all real and stored.", icon: Sparkles },
            { step: "03", title: "Live inside it", desc: "Daily missions, XP, streaks, evolving districts, mentor sessions and simulations. Your future becomes a place you visit daily.", icon: Globe2 },
          ].map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              className="glass relative rounded-3xl p-6"
            >
              <div className="font-display absolute -top-3 left-5 rounded-lg bg-gradient-to-r from-violet-500 to-cyan-400 px-2.5 py-0.5 text-xs font-black text-black">{s.step}</div>
              <s.icon className="mb-3 mt-2 h-7 w-7 text-primary" />
              <h3 className="font-display text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 mx-auto mt-28 max-w-7xl px-6">
        <div className="mb-10 text-center">
          <h2 className="font-display text-2xl font-bold sm:text-4xl">A complete AI operating system</h2>
          <p className="mt-3 text-muted-foreground">Every part is real: stored, editable, and connected to the AI&apos;s tool system.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.08, duration: 0.5 }}
              className="glass group relative overflow-hidden rounded-3xl p-5 transition-all hover:border-primary/30 hover:shadow-[0_0_36px_rgba(139,92,246,0.15)]"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${f.accent} opacity-0 transition-opacity group-hover:opacity-100`} />
              <div className="relative">
                <f.icon className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-display text-base font-semibold">{f.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 mx-auto mt-28 max-w-4xl px-6 pb-24 text-center">
        <div className="glass relative overflow-hidden rounded-[2rem] px-8 py-14">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-cyan-500/10" />
          <h2 className="relative font-display text-3xl font-black sm:text-4xl">
            Your future self is <span className="text-gradient">already waiting</span>
          </h2>
          <p className="relative mx-auto mt-4 max-w-xl text-muted-foreground">
            Every empire, every skill, every shipped game started as one sentence someone dared to say out loud.
            Say yours.
          </p>
          <Button
            size="lg"
            onClick={() => (loggedIn ? router.push("/dream/new") : setLoginOpen(true))}
            className="relative mt-8 h-13 rounded-2xl bg-gradient-to-r from-violet-500 to-cyan-500 px-10 text-base font-semibold text-black shadow-[0_0_40px_rgba(139,92,246,0.5)] hover:from-violet-400 hover:to-cyan-400"
          >
            Begin your first dream
            <ChevronRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/40 py-8 text-center text-xs text-muted-foreground">
        Dream Builder AI — the operating system for turning human dreams into real projects.
      </footer>

      <LoginDialog open={loginOpen} onOpenChange={(v) => { setLoginOpen(v); if (!v) setChecking(false); }} />
      {checking && <span className="hidden" aria-hidden />}
    </div>
  );
}

export default function LandingPage() {
  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 font-black text-black shadow-[0_0_20px_rgba(139,92,246,0.5)]">✦</div>
      </div>
    }>
      <LandingPageContent />
    </Suspense>
  );
}
